import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { partsAPI, customersAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Plus, Search, Cable, Eye, Pencil, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function WirePartForm({ onSubmit, onCancel, editPart = null }) {
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [form, setForm] = useState({
    internal_part_no: editPart?.internal_part_no || '',
    customer_part_no: editPart?.customer_part_no || '',
    description: editPart?.description || '',
    hs_code: editPart?.hs_code || '',
    wire_diameter_min_mm: editPart?.wire_diameter_min_mm || '',
    wire_diameter_max_mm: editPart?.wire_diameter_max_mm || '',
    wire_grade: editPart?.wire_grade || '',
    wire_coil_weight_kg: editPart?.wire_coil_weight_kg || '',
    wire_uts_min_mpa: editPart?.wire_uts_min_mpa || '',
    wire_uts_max_mpa: editPart?.wire_uts_max_mpa || '',
    wire_hardness_notes: editPart?.wire_hardness_notes || '',
    customer_id: editPart?.customer_id || '',
    customer_name: editPart?.customer_name || '',
  });

  useEffect(() => {
    customersAPI.list().then((r) => setCustomers(r.data.customers || r.data || []));
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const filteredCustomers = customers.filter((c) =>
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.customer_code?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const NUM = new Set(['wire_diameter_min_mm', 'wire_diameter_max_mm', 'wire_coil_weight_kg', 'wire_uts_min_mpa', 'wire_uts_max_mpa']);
    const data = { product_category: 'wire', part_type: 'Wire' };
    Object.entries(form).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) {
        data[k] = NUM.has(k) ? parseFloat(v) : v;
      }
    });
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="wire-part-form">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Internal Part No. *</Label>
          <Input required value={form.internal_part_no} onChange={(e) => setField('internal_part_no', e.target.value)} placeholder="e.g., WIRE-5.5-72A" data-testid="wp-internal" />
        </div>
        <div className="space-y-1.5">
          <Label>Customer Part No.</Label>
          <Input value={form.customer_part_no} onChange={(e) => setField('customer_part_no', e.target.value)} />
        </div>
        <div className="col-span-2 space-y-1.5">
          <Label>Description *</Label>
          <Input required value={form.description} onChange={(e) => setField('description', e.target.value)} placeholder="e.g., 5.5mm SWRH72A Patented Wire — for spring applications" data-testid="wp-description" />
        </div>
      </div>

      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
        <h4 className="text-sm font-bold uppercase tracking-wide text-blue-900">Wire Specifications</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Diameter Min (mm) *</Label><Input type="number" step="0.01" required value={form.wire_diameter_min_mm} onChange={(e) => setField('wire_diameter_min_mm', e.target.value)} placeholder="5.45" data-testid="wp-dia-min" /></div>
          <div className="space-y-1.5"><Label>Diameter Max (mm) *</Label><Input type="number" step="0.01" required value={form.wire_diameter_max_mm} onChange={(e) => setField('wire_diameter_max_mm', e.target.value)} placeholder="5.55" /></div>
          <div className="space-y-1.5"><Label>Wire Grade *</Label><Input required value={form.wire_grade} onChange={(e) => setField('wire_grade', e.target.value)} placeholder="SWRH72A, 1010, 10B21…" data-testid="wp-grade" /></div>
          <div className="space-y-1.5"><Label>Standard Coil Weight (Kg)</Label><Input type="number" step="0.01" value={form.wire_coil_weight_kg} onChange={(e) => setField('wire_coil_weight_kg', e.target.value)} placeholder="1500" /></div>
          <div className="space-y-1.5"><Label>UTS Min (MPa)</Label><Input type="number" step="1" value={form.wire_uts_min_mpa} onChange={(e) => setField('wire_uts_min_mpa', e.target.value)} placeholder="1180" /></div>
          <div className="space-y-1.5"><Label>UTS Max (MPa)</Label><Input type="number" step="1" value={form.wire_uts_max_mpa} onChange={(e) => setField('wire_uts_max_mpa', e.target.value)} placeholder="1370" /></div>
          <div className="col-span-2 space-y-1.5"><Label>Hardness / Special Requirements</Label><Input value={form.wire_hardness_notes} onChange={(e) => setField('wire_hardness_notes', e.target.value)} placeholder="e.g., HRB 75-85, fully annealed, low residual stress" /></div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>HS Code</Label>
          <Input value={form.hs_code} onChange={(e) => setField('hs_code', e.target.value)} placeholder="e.g., 72171000" />
        </div>
        <div className="space-y-1.5">
          <Label>Customer</Label>
          <div className="relative">
            {form.customer_id ? (
              <div className="flex items-center justify-between p-2 bg-muted/30 rounded border">
                <span className="text-sm font-medium">{form.customer_name}</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => { setField('customer_id', ''); setField('customer_name', ''); }}>×</Button>
              </div>
            ) : (
              <>
                <Input value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} placeholder="Search customer…" />
                {customerSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredCustomers.slice(0, 10).map((c) => (
                      <div key={c.id} className="p-2 hover:bg-muted cursor-pointer" onClick={() => { setField('customer_id', c.id); setField('customer_name', c.name); setCustomerSearch(''); }}>
                        <div className="text-sm font-medium">{c.name}</div>
                        <div className="text-xs text-muted-foreground">{c.customer_code}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-3 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="btn-primary" data-testid="wp-save-btn">{editPart ? 'Update Wire Part' : 'Create Wire Part'}</Button>
      </div>
    </form>
  );
}

export default function WirePartsPage() {
  const navigate = useNavigate();
  const [parts, setParts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPart, setEditPart] = useState(null);

  const load = () => {
    setLoading(true);
    partsAPI.list({ product_category: 'wire', limit: 200, search: search || undefined })
      .then((r) => setParts(r.data.items || r.data.data || []))
      .catch(() => toast.error('Failed to load wire parts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const handleCreate = async (data) => {
    try {
      await partsAPI.create(data);
      toast.success('Wire part created');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create wire part');
    }
  };

  const handleUpdate = async (data) => {
    try {
      await partsAPI.update(editPart.id, data);
      toast.success('Wire part updated');
      setEditPart(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wire-parts-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight flex items-center gap-3">
            <Cable className="h-7 w-7 text-blue-600" />
            Wire Parts
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Per-kg costing parts (PPD / SAPPD wire grades)</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-wire-part-btn"><Plus className="h-4 w-4 mr-2" />Add Wire Part</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Wire Part</DialogTitle>
              <DialogDescription>Per-kg pricing — used for wire quotations</DialogDescription>
            </DialogHeader>
            <WirePartForm onSubmit={handleCreate} onCancel={() => setDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search by part no, grade, description…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="wire-search" />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Internal P/N</TableHead>
                  <TableHead>Customer P/N</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Diameter (mm)</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead className="text-right">Coil Wt</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parts.map((p) => (
                  <TableRow key={p.id} className="hover:bg-muted/40" data-testid={`wire-part-row-${p.id}`}>
                    <TableCell className="font-mono font-medium">{p.internal_part_no}</TableCell>
                    <TableCell className="font-mono text-xs">{p.customer_part_no || '-'}</TableCell>
                    <TableCell className="max-w-md truncate">{p.description}</TableCell>
                    <TableCell className="font-mono">{p.wire_diameter_min_mm || '-'} – {p.wire_diameter_max_mm || '-'}</TableCell>
                    <TableCell>{p.wire_grade || '-'}</TableCell>
                    <TableCell className="text-right font-mono">{p.wire_coil_weight_kg ? `${p.wire_coil_weight_kg} kg` : '-'}</TableCell>
                    <TableCell>{p.customer_name || '-'}</TableCell>
                    <TableCell><Badge className={p.is_active ? 'badge-success' : 'badge-error'}>{p.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/parts/${p.id}`)} title="View 360°"><Eye className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditPart(p)} title="Edit"><Pencil className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {parts.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No wire parts yet. Click <strong>Add Wire Part</strong> to create one.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editPart} onOpenChange={(o) => !o && setEditPart(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Wire Part</DialogTitle>
            <DialogDescription>{editPart?.internal_part_no}</DialogDescription>
          </DialogHeader>
          {editPart && <WirePartForm editPart={editPart} onSubmit={handleUpdate} onCancel={() => setEditPart(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
